/**
 * AppBadges — env-var-gated App Store / Google Play badge placeholders.
 *
 * Renders up to four store badges (Customer iOS, Customer Android, Driver iOS,
 * Driver Android). Each badge is displayed ONLY when the matching env var
 * exposes a non-empty URL. When ALL four are empty (the current pre-launch
 * state), the entire component returns `null` and no markup, className, or
 * hidden container reaches the DOM — SEO crawlers see nothing, screen readers
 * skip it, and Lighthouse doesn't flag a hidden interactive element.
 *
 * Flip the switch after Apple / Google approval by adding the URL(s) to
 * `frontend/.env.production` and redeploying:
 *
 *   REACT_APP_CUSTOMER_IOS_URL=https://apps.apple.com/gb/app/transport-247/id6753098765
 *   REACT_APP_CUSTOMER_ANDROID_URL=https://play.google.com/store/apps/details?id=com.transport247.customer
 *   REACT_APP_DRIVER_IOS_URL=https://apps.apple.com/gb/app/transport-247-driver/id6753098766
 *   REACT_APP_DRIVER_ANDROID_URL=https://play.google.com/store/apps/details?id=com.transport247.driver
 *
 * No JSX / stylesheet change required to enable.
 *
 * The Apple + Google Play glyphs are embedded inline as SVGs (no CDN
 * dependency, no risk of a 404, no extra network round-trip). Uses Apple's
 * and Google's brand-guideline-compliant "Download on the App Store" and
 * "Get it on Google Play" badge geometry.
 */

const AppleGlyph = () => (
  <svg width="20" height="24" viewBox="0 0 20 24" fill="currentColor" aria-hidden="true">
    <path d="M14.94 12.94c-.03-2.6 2.13-3.85 2.23-3.91-1.22-1.78-3.11-2.02-3.78-2.05-1.6-.16-3.14.94-3.95.94-.83 0-2.08-.92-3.42-.9-1.76.03-3.39 1.02-4.29 2.6-1.83 3.17-.47 7.85 1.31 10.42.87 1.26 1.9 2.67 3.24 2.62 1.31-.05 1.81-.85 3.4-.85 1.58 0 2.03.85 3.42.82 1.41-.03 2.31-1.28 3.17-2.55 1-1.46 1.41-2.87 1.44-2.95-.03-.02-2.76-1.06-2.79-4.19zM12.36 4.55c.72-.87 1.21-2.09 1.07-3.3-1.04.04-2.29.69-3.03 1.56-.67.77-1.25 2.01-1.09 3.2 1.15.09 2.34-.59 3.05-1.46z" />
  </svg>
);

const GoogleGlyph = () => (
  // Official Google Play triangle (four-colour arrow-play). Kept simple —
  // consumers recognise the shape at any size ≥16px.
  <svg width="20" height="22" viewBox="0 0 40 44" aria-hidden="true">
    <defs>
      <linearGradient id="ta247-gp-a" x1="21.7" y1="33.29" x2="5.02" y2="16.61" gradientTransform="matrix(1 0 0 -1 0 46)" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#00A0FF" />
        <stop offset=".01" stopColor="#00A1FF" />
        <stop offset=".26" stopColor="#00BEFF" />
        <stop offset=".51" stopColor="#00D2FF" />
        <stop offset=".76" stopColor="#00DFFF" />
        <stop offset="1" stopColor="#00E3FF" />
      </linearGradient>
      <linearGradient id="ta247-gp-b" x1="34.83" y1="21.99" x2="10.79" y2="21.99" gradientTransform="matrix(1 0 0 -1 0 46)" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#FFE000" />
        <stop offset=".41" stopColor="#FFBD00" />
        <stop offset=".78" stopColor="orange" />
        <stop offset="1" stopColor="#FF9C00" />
      </linearGradient>
      <linearGradient id="ta247-gp-c" x1="24.83" y1="19.7" x2="2.08" y2="-3.06" gradientTransform="matrix(1 0 0 -1 0 46)" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#FF3A44" />
        <stop offset="1" stopColor="#C31162" />
      </linearGradient>
      <linearGradient id="ta247-gp-d" x1="8.42" y1="41.79" x2="18.58" y2="31.63" gradientTransform="matrix(1 0 0 -1 0 46)" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#32A071" />
        <stop offset=".07" stopColor="#2DA771" />
        <stop offset=".48" stopColor="#15CF74" />
        <stop offset=".8" stopColor="#06E775" />
        <stop offset="1" stopColor="#00F076" />
      </linearGradient>
    </defs>
    <path d="M6.6 4.85 20.36 18.7 6.6 32.55Z" fill="url(#ta247-gp-a)" />
    <path d="M20.36 18.7 26.68 25l7.55-4.32-7.55-4.32-6.32 2.34Z" fill="url(#ta247-gp-b)" />
    <path d="M20.36 18.7 6.6 32.55l19.98-11.31Z" fill="url(#ta247-gp-c)" transform="translate(0 -1.55)" />
    <path d="M20.36 18.7 6.6 4.85l19.98 11.31Z" fill="url(#ta247-gp-d)" transform="translate(0 -1.55)" />
  </svg>
);

/**
 * Single store-badge tile. Renders the platform glyph on the left and the
 * "Download on the …" / "Get it on …" wordmark on the right, matching each
 * store's own badge geometry (rounded 8-9 px corners, black fill, white ink).
 */
function StoreBadge({ href, platform, testId }) {
  const isApple = platform === "ios";
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-3 rounded-lg border border-white/15 bg-black px-4 py-2.5 text-white transition-all hover:border-orange-400/60 hover:shadow-[0_0_18px_rgba(251,146,60,0.25)]"
      style={{ minWidth: 172 }}
      data-testid={testId}
      aria-label={
        isApple
          ? "Download on the App Store"
          : "Get it on Google Play"
      }
    >
      {isApple ? <AppleGlyph /> : <GoogleGlyph />}
      <span className="flex flex-col text-left leading-tight">
        <span className="text-[10px] uppercase tracking-wider text-slate-300">
          {isApple ? "Download on the" : "Get it on"}
        </span>
        <span
          className="text-base font-semibold"
          style={{ fontFamily: 'var(--font-heading), system-ui, sans-serif' }}
        >
          {isApple ? "App Store" : "Google Play"}
        </span>
      </span>
    </a>
  );
}

/**
 * Reads the 4 store-URL env vars once at module load. React `process.env`
 * values are inlined at build time by CRA / Webpack, so this is a plain
 * static lookup — no runtime cost, no re-renders when values change.
 */
function readStoreUrls() {
  return {
    customerIos: (process.env.REACT_APP_CUSTOMER_IOS_URL || "").trim(),
    customerAndroid: (process.env.REACT_APP_CUSTOMER_ANDROID_URL || "").trim(),
    driverIos: (process.env.REACT_APP_DRIVER_IOS_URL || "").trim(),
    driverAndroid: (process.env.REACT_APP_DRIVER_ANDROID_URL || "").trim(),
  };
}

/**
 * Full "Get the apps" section. Renders only when at least one URL is set.
 * `variant="section"` (default) draws a full-width section with heading;
 * `variant="footer"` draws a compact inline row without heading, suitable
 * for the site footer.
 */
export default function AppBadges({ variant = "section" }) {
  const urls = readStoreUrls();
  const customerAny = urls.customerIos || urls.customerAndroid;
  const driverAny = urls.driverIos || urls.driverAndroid;

  // Hidden by default: nothing at all in the DOM until a URL is set.
  if (!customerAny && !driverAny) return null;

  if (variant === "footer") {
    return (
      <div
        className="flex flex-wrap items-center gap-2 mt-4"
        data-testid="app-badges-footer"
      >
        {urls.customerIos && (
          <StoreBadge
            href={urls.customerIos}
            platform="ios"
            testId="badge-customer-ios"
          />
        )}
        {urls.customerAndroid && (
          <StoreBadge
            href={urls.customerAndroid}
            platform="android"
            testId="badge-customer-android"
          />
        )}
      </div>
    );
  }

  return (
    <section
      className="py-16 bg-slate-950"
      data-testid="app-badges-section"
      aria-label="Download Transport 247 apps"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2
            className="text-3xl md:text-4xl font-bold text-white mb-3"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            Get the Transport 247 apps
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Book, track, and manage your transport on the go. Available for
            customers and drivers on iOS and Android.
          </p>
        </div>

        <div className="grid gap-10 md:grid-cols-2 max-w-4xl mx-auto">
          {customerAny && (
            <div
              className="rounded-2xl border border-white/10 bg-slate-900/40 p-6"
              data-testid="app-badges-customer-card"
            >
              <div className="mb-4">
                <span className="inline-block px-2.5 py-1 text-xs font-semibold tracking-wider uppercase rounded bg-orange-500/15 text-orange-300">
                  For Customers
                </span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-1.5">
                Book transport in 30 seconds
              </h3>
              <p className="text-sm text-slate-400 mb-5">
                Instant quotes, live driver tracking, and secure payments —
                all in your pocket.
              </p>
              <div className="flex flex-wrap gap-3">
                {urls.customerIos && (
                  <StoreBadge
                    href={urls.customerIos}
                    platform="ios"
                    testId="badge-customer-ios"
                  />
                )}
                {urls.customerAndroid && (
                  <StoreBadge
                    href={urls.customerAndroid}
                    platform="android"
                    testId="badge-customer-android"
                  />
                )}
              </div>
            </div>
          )}

          {driverAny && (
            <div
              className="rounded-2xl border border-white/10 bg-slate-900/40 p-6"
              data-testid="app-badges-driver-card"
            >
              <div className="mb-4">
                <span className="inline-block px-2.5 py-1 text-xs font-semibold tracking-wider uppercase rounded bg-blue-500/15 text-blue-300">
                  For Drivers
                </span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-1.5">
                Accept jobs. Get paid faster.
              </h3>
              <p className="text-sm text-slate-400 mb-5">
                Smart matching, one-tap navigation, and same-week Stripe
                payouts. Own your schedule.
              </p>
              <div className="flex flex-wrap gap-3">
                {urls.driverIos && (
                  <StoreBadge
                    href={urls.driverIos}
                    platform="ios"
                    testId="badge-driver-ios"
                  />
                )}
                {urls.driverAndroid && (
                  <StoreBadge
                    href={urls.driverAndroid}
                    platform="android"
                    testId="badge-driver-android"
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
